import { AxiosResponse } from 'axios';
import api from '../api';
import {
  IHomeworkClassroomsResponse,
  IHomeworkClassroomSubjectsResponse,
  IHomeworkClassroomSubjectTeachersResponse,
  ICreateHomeworkPayload,
  ICreateHomeworkResponse,
  IHomeworkDashboardQueryParams,
  IHomeworkDashboardResponse,
  IGetHomeworkByIdResponse,
  IEditHomeworkPayload,
  IEditHomeworkResponse,
  IHomeworkStatusChangeResponse,
  ITaskSubmissionsResponse,
  ITaskSubmissionsQueryParams,
  IGetTaskSubmissionDetailResponse,
  IGradeTaskSubmissionPayload,
  IReleaseTaskResponse,
  IMyClassroomsResponse,
  IMySubjectsResponse,
} from '../../interface/homework.interface';
import {
  IDiscussionPaginationParams,
  IDiscussionRepliesResponse,
  IDiscussionRootsResponse,
  IPostDiscussionPayload,
  IPostDiscussionResponse,
} from '../../interface/homeworkDiscussion.interface';

const fetchHomeworkDashboard = async (
  params?: IHomeworkDashboardQueryParams,
): Promise<AxiosResponse<IHomeworkDashboardResponse>> => {
  // Role-adaptive list endpoint: admins get org-wide results, teachers get their
  // own (assigned + Primary/Acting classroom) homeworks. Same response shape as
  // the legacy /homework list.
  const response = await api.get<IHomeworkDashboardResponse>('/me/homework', { params });
  return response;
};

const fetchClassroomsByGradeAndCurriculum = async (params: {
  curriculumId: string;
  gradeId: string;
}): Promise<AxiosResponse<IHomeworkClassroomsResponse>> => {
  const response = await api.get<IHomeworkClassroomsResponse>('/subject/classroom', {
    params: {
      isActive: true,
      curriculumId: params.curriculumId,
      gradeId: params.gradeId,
    },
  });
  return response;
};

/**
 * Role-aware classroom source. Curriculum / Grade / Classroom options are derived
 * client-side from this single list.
 * - Teacher: pass `{ isSubject: true }` → her roster subject-classrooms.
 * - Admin: pass nothing → all org classrooms (the `isSubject` filter is ignored).
 */
const fetchMyClassrooms = async (params?: {
  isSubject?: boolean;
}): Promise<AxiosResponse<IMyClassroomsResponse>> => {
  const response = await api.get<IMyClassroomsResponse>('/me/classrooms', { params });
  return response;
};

/**
 * The logged-in teacher's roster subjects for a classroom (TEACHER-ONLY; 403 for
 * other roles). Each entry carries the `subjectAssignId` used as the subject value.
 */
const fetchMySubjectsByClassroom = async (
  classroomId: string,
  includeAssignId?: string,
): Promise<AxiosResponse<IMySubjectsResponse>> => {
  const response = await api.get<IMySubjectsResponse>('/me', {
    params: { type: 'subjects', classroomId, ...(includeAssignId && { includeAssignId }) },
  });
  return response;
};

const fetchSubjectsByClassroom = async (
  classroomId: string,
  includeAssignId?: string,
): Promise<AxiosResponse<IHomeworkClassroomSubjectsResponse>> => {
  const response = await api.get<IHomeworkClassroomSubjectsResponse>(
    `/classroom/${classroomId}/subjects`,
    { params: includeAssignId ? { includeAssignId } : undefined },
  );
  return response;
};

const fetchTeachersByClassroomSubject = async (params: {
  classroomId: string;
  subjectAssignId: string;
}): Promise<AxiosResponse<IHomeworkClassroomSubjectTeachersResponse>> => {
  const response = await api.get<IHomeworkClassroomSubjectTeachersResponse>(
    `/classroom/${params.classroomId}/subject-teacher`,
    { params: { subjectAssignId: params.subjectAssignId } },
  );
  return response;
};

const createHomework = async (
  payload: ICreateHomeworkPayload,
): Promise<AxiosResponse<ICreateHomeworkResponse>> => {
  const response = await api.post<ICreateHomeworkResponse>('/homework', payload);
  return response;
};

const getHomeworkById = async (
  homeworkId: string,
): Promise<AxiosResponse<IGetHomeworkByIdResponse>> => {
  const response = await api.get<IGetHomeworkByIdResponse>(`/homework/${homeworkId}`);
  return response;
};

const editHomework = async (
  homeworkId: string,
  payload: IEditHomeworkPayload,
): Promise<AxiosResponse<IEditHomeworkResponse>> => {
  const response = await api.put<IEditHomeworkResponse>(`/homework/${homeworkId}`, payload);
  return response;
};

// Soft-delete a homework (and its related data). Access is enforced server-side
// by role + status; the list/detail `canDelete` flag only gates the button.
const deleteHomework = async (
  homeworkId: string,
): Promise<AxiosResponse<IHomeworkStatusChangeResponse>> => {
  const response = await api.delete<IHomeworkStatusChangeResponse>(`/homework/${homeworkId}`);
  return response;
};

// Clone an existing homework into a new DRAFT copy; returns the new id.
const duplicateHomework = async (
  homeworkId: string,
): Promise<AxiosResponse<ICreateHomeworkResponse>> => {
  const response = await api.post<ICreateHomeworkResponse>(`/homework/${homeworkId}/duplicate`);
  return response;
};

// DRAFT → PUBLISHED transition.
const publishHomework = async (
  homeworkId: string,
): Promise<AxiosResponse<IHomeworkStatusChangeResponse>> => {
  const response = await api.patch<IHomeworkStatusChangeResponse>(
    `/homework/${homeworkId}/publish`,
  );
  return response;
};

// PUBLISHED → GRADING (manual start; GRADE_AND_RESPOND only).
const startGrading = async (
  homeworkId: string,
): Promise<AxiosResponse<IHomeworkStatusChangeResponse>> => {
  const response = await api.patch<IHomeworkStatusChangeResponse>(
    `/homework/${homeworkId}/start-grading`,
  );
  return response;
};

const fetchTaskSubmissions = async (
  homeworkId: string,
  detailId: string,
  params?: ITaskSubmissionsQueryParams,
): Promise<AxiosResponse<ITaskSubmissionsResponse>> => {
  const response = await api.get<ITaskSubmissionsResponse>(
    `/homework/${homeworkId}/tasks/${detailId}/submissions`,
    { params },
  );
  return response;
};

const fetchTaskSubmissionDetail = async (
  homeworkId: string,
  detailId: string,
  studentId: string,
): Promise<AxiosResponse<IGetTaskSubmissionDetailResponse>> => {
  const response = await api.get<IGetTaskSubmissionDetailResponse>(
    `/homework/${homeworkId}/tasks/${detailId}/submissions/${studentId}`,
  );
  return response;
};

const gradeTaskSubmission = async (
  homeworkId: string,
  detailId: string,
  studentId: string,
  payload: IGradeTaskSubmissionPayload,
): Promise<AxiosResponse<IHomeworkStatusChangeResponse>> => {
  const response = await api.patch<IHomeworkStatusChangeResponse>(
    `/homework/${homeworkId}/tasks/${detailId}/submissions/${studentId}`,
    payload,
  );
  return response;
};

// Release one task's scores (auto-releases the homework when all tasks done).
const releaseTask = async (
  homeworkId: string,
  detailId: string,
): Promise<AxiosResponse<IReleaseTaskResponse>> => {
  const response = await api.patch<IReleaseTaskResponse>(
    `/homework/${homeworkId}/tasks/${detailId}/release`,
  );
  return response;
};

// --- Homework Discussion (threaded conversations) ---
// Base URL already includes `/v2`. Pagination is optional on both GETs — omit
// `page`/`pageSize` to get ALL rows.

// GET roots: each carries `replyCount` + `latestReply` (no full replies array).
const fetchHomeworkDiscussion = async (
  homeworkId: string,
  params?: IDiscussionPaginationParams,
): Promise<AxiosResponse<IDiscussionRootsResponse>> => {
  const response = await api.get<IDiscussionRootsResponse>(`/homework/${homeworkId}/discussion`, {
    params,
  });
  return response;
};

// GET the full replies of one root, lazily (on "See all N replies").
const fetchDiscussionReplies = async (
  homeworkId: string,
  rootId: string,
  params?: IDiscussionPaginationParams,
): Promise<AxiosResponse<IDiscussionRepliesResponse>> => {
  const response = await api.get<IDiscussionRepliesResponse>(
    `/homework/${homeworkId}/discussion/${rootId}/replies`,
    { params },
  );
  return response;
};

// POST a root message (no `parentId`) or a reply (`parentId` = a root's id).
const postHomeworkDiscussion = async (
  homeworkId: string,
  payload: IPostDiscussionPayload,
): Promise<AxiosResponse<IPostDiscussionResponse>> => {
  const response = await api.post<IPostDiscussionResponse>(
    `/homework/${homeworkId}/discussion`,
    payload,
  );
  return response;
};

export const homeworkService = {
  fetchHomeworkDashboard,
  fetchClassroomsByGradeAndCurriculum,
  fetchMyClassrooms,
  fetchMySubjectsByClassroom,
  fetchSubjectsByClassroom,
  fetchTeachersByClassroomSubject,
  createHomework,
  getHomeworkById,
  editHomework,
  deleteHomework,
  duplicateHomework,
  publishHomework,
  startGrading,
  fetchTaskSubmissions,
  fetchTaskSubmissionDetail,
  gradeTaskSubmission,
  releaseTask,
  fetchHomeworkDiscussion,
  fetchDiscussionReplies,
  postHomeworkDiscussion,
};
